import React, { useEffect, useRef, useState } from 'react'
import { Circle, Group, Image as KonvaImage, Rect, Text } from 'react-konva'
import type { Problem } from '../types'
import { CARD_WIDTH, PROBLEM_OFFSET_X, PROBLEM_OFFSET_Y, COLORS } from '../constants'
import type { ImageMap } from '../hooks/useImageLoader'

// right.png 스프라이트: 1000×800, 5열×4행 = 20프레임, 프레임 200×200
const SPRITE_COLS = 5
const SPRITE_ROWS = 4
const SPRITE_FRAME_W = 200
const SPRITE_FRAME_H = 200
const SPRITE_TOTAL_FRAMES = SPRITE_COLS * SPRITE_ROWS

interface ProblemDisplayProps {
  problem: Problem
  images?: ImageMap | null
  doneCount?: number
  looseCount?: number
  completed?: boolean
  allowCustomInput?: boolean
  onSaveProblem?: (left: number, right: number) => void
  onReset?: () => void
  /** 정답 애니메이션 중 십의자리 카운트 (null이면 "?" 유지) */
  animTens?: number | null
  /** 정답 애니메이션 중 일의자리 카운트 (null이면 "?" 유지) */
  animOnes?: number | null
}

// 카드 전체 높이 & 행 분할
const CARD_HEIGHT = 175
const PROBLEM_ROW_HEIGHT = 88 // 문제 행 높이
const ANSWER_ROW_HEIGHT = CARD_HEIGHT - PROBLEM_ROW_HEIGHT // 정답 행 높이

// 내부 레이아웃 좌표
const LABEL_X = 20
const ROW_MID_Y = PROBLEM_ROW_HEIGHT / 2 // 문제 행 중앙 (44)
const ANSWER_MID_Y = PROBLEM_ROW_HEIGHT + ANSWER_ROW_HEIGHT / 2 // 정답 행 중앙 (131)

// 수식/답칸 중앙 영역 (라벨~리셋 사이 가운데 정렬)
const CONTENT_LEFT = 75
const CONTENT_RIGHT = CARD_WIDTH - 55 // 리셋 버튼 여백 확보
const CONTENT_CENTER = (CONTENT_LEFT + CONTENT_RIGHT) / 2
const NUM_SPACING = 50

// 색상 (Figma 기반)
const C = {
  problemBg: '#FFFFFF',       // 문제 행 배경 (흰색)
  answerBg: '#F0F0F0',        // 정답 행 배경 (연회색)
  labelProblem: '#7B9B6B',    // "문제" 라벨 (올리브 그린)
  labelAnswer: '#888888',     // "정답" 라벨 (회색)
  number: '#3E2723',          // 숫자 (진갈색)
  multiply: '#666666',        // × 기호
  sipText: '#888888',         // "십" 텍스트
  circleQuestion: '#999999',  // "?" 텍스트
  circleBg: '#FFFFFF',        // 답칸 원 배경
  circleBorder: '#E0E0E0',    // 답칸 원 테두리
  stepperBg: '#E0E0E0',       // 스테퍼 버튼 배경
  stepperBgDisabled: '#F0F0F0',
  stepperText: '#666666',
  stepperTextDisabled: '#BDBDBD',
} as const

function numImgKey(digit: number, isOn: boolean): keyof ImageMap {
  const d = Math.max(0, Math.min(9, digit))
  return `num${d}${isOn ? 'On' : 'Off'}` as keyof ImageMap
}

export const ProblemDisplay: React.FC<ProblemDisplayProps> = ({
  problem,
  images,
  doneCount = 0,
  looseCount = 0,
  completed = false,
  allowCustomInput = false,
  onSaveProblem,
  onReset,
  animTens = null,
  animOnes = null,
}) => {
  const tens = Math.floor(doneCount / 10)
  const ones = looseCount
  const hasTied = doneCount > 0

  const leftNumX = CONTENT_CENTER - NUM_SPACING
  const rightNumX = CONTENT_CENTER + NUM_SPACING

  return (
    <Group x={PROBLEM_OFFSET_X} y={PROBLEM_OFFSET_Y}>
      {/* ─── 문제 행 배경 (흰색, 상단 라운드) ─── */}
      <Rect
        x={0} y={0}
        width={CARD_WIDTH} height={PROBLEM_ROW_HEIGHT}
        fill={C.problemBg}
        cornerRadius={[12, 12, 0, 0]}
        listening={false}
      />
      {/* ─── 정답 행 배경 (연회색, 하단 라운드) ─── */}
      <Rect
        x={0} y={PROBLEM_ROW_HEIGHT}
        width={CARD_WIDTH} height={ANSWER_ROW_HEIGHT}
        fill={C.answerBg}
        cornerRadius={[0, 0, 12, 12]}
        listening={false}
      />

      {/* ─── 문제 행 ─── */}
      <Text
        x={LABEL_X} y={ROW_MID_Y - 12}
        text="문제"
        fontSize={20} fontStyle="bold" fontFamily="CookeiRunBold"
        fill={C.labelProblem}
        listening={false}
      />
      {/* × 기호 */}
      <Text
        x={CONTENT_CENTER - 15} y={ROW_MID_Y - 20}
        width={30} height={40}
        text="×"
        fontSize={36} fontStyle="bold" fontFamily="CookeiRunBlack"
        fill={C.multiply} align="center" verticalAlign="middle"
        listening={false}
      />
      {/* Left number */}
      {allowCustomInput && !completed ? (
        <NumberStepper
          x={leftNumX} y={ROW_MID_Y}
          value={problem.left}
          onChange={(delta) => {
            const newVal = Math.min(9, Math.max(2, problem.left + delta))
            if (newVal !== problem.left) onSaveProblem?.(newVal, problem.right)
          }}
        />
      ) : (
        <Text
          x={leftNumX - 25} y={ROW_MID_Y - 20}
          width={50} height={40}
          text={String(problem.left)}
          fontSize={40} fontStyle="bold" fontFamily="CookeiRunBlack"
          fill={C.number} align="center" verticalAlign="middle"
          listening={false}
        />
      )}
      {/* Right number */}
      {allowCustomInput && !completed ? (
        <NumberStepper
          x={rightNumX} y={ROW_MID_Y}
          value={problem.right}
          onChange={(delta) => {
            const newVal = Math.min(9, Math.max(2, problem.right + delta))
            if (newVal !== problem.right) onSaveProblem?.(problem.left, newVal)
          }}
        />
      ) : (
        <Text
          x={rightNumX - 25} y={ROW_MID_Y - 20}
          width={50} height={40}
          text={String(problem.right)}
          fontSize={40} fontStyle="bold" fontFamily="CookeiRunBlack"
          fill={C.number} align="center" verticalAlign="middle"
          listening={false}
        />
      )}

      {/* ─── 정답 행 ─── */}
      <Text
        x={LABEL_X} y={ANSWER_MID_Y - 12}
        text="정답"
        fontSize={20} fontStyle="bold" fontFamily="CookeiRunBold"
        fill={C.labelAnswer}
        listening={false}
      />
      {/* 십 텍스트 */}
      <Text
        x={CONTENT_CENTER - 15} y={ANSWER_MID_Y - 12}
        width={30} height={24}
        text="십"
        fontSize={20} fontStyle="bold" fontFamily="CookeiRunBold"
        fill={C.sipText} align="center" verticalAlign="middle"
        listening={false}
      />
      {/* 십의 자리 */}
      {completed ? (
        <AnswerCircle x={leftNumX} y={ANSWER_MID_Y} digit={tens} isOn={true} images={images} />
      ) : animTens !== null ? (
        <AnswerCircle x={leftNumX} y={ANSWER_MID_Y} digit={animTens} isOn={false} images={images} />
      ) : hasTied ? (
        <AnswerCircle x={leftNumX} y={ANSWER_MID_Y} digit={tens} isOn={false} images={images} />
      ) : (
        <AnswerCircle x={leftNumX} y={ANSWER_MID_Y} digit={-1} isOn={false} images={images} />
      )}
      {/* 일의 자리 */}
      {completed ? (
        <AnswerCircle x={rightNumX} y={ANSWER_MID_Y} digit={ones} isOn={true} images={images} />
      ) : animOnes !== null ? (
        <AnswerCircle x={rightNumX} y={ANSWER_MID_Y} digit={animOnes} isOn={false} images={images} />
      ) : (
        <AnswerCircle x={rightNumX} y={ANSWER_MID_Y} digit={-1} isOn={false} images={images} />
      )}

      {/* 정답 동그라미 스프라이트 애니메이션 */}
      {completed && (
        <RightAnimation x={CONTENT_CENTER} y={ANSWER_MID_Y} images={images} />
      )}

      {/* ─── 리셋 버튼 (정답 행 우측) ─── */}
      {onReset && (
        <Group onClick={onReset} onTap={onReset}>
          <Circle
            x={CARD_WIDTH - 28} y={ANSWER_MID_Y}
            radius={16}
            fill={COLORS.resetButton}
          />
          <Text
            x={CARD_WIDTH - 28 - 16} y={ANSWER_MID_Y - 15}
            width={32} height={32}
            text="↻" fontSize={22} fontStyle="bold"
            fill="#FFFFFF" align="center" verticalAlign="top"
            offsetY={-2}
            listening={false}
          />
        </Group>
      )}
    </Group>
  )
}

/** 정답 원: 배경 + 숫자 이미지 (digit=-1이면 "?" 표시) */
function AnswerCircle({ x, y, digit, isOn, images }: {
  x: number; y: number; digit: number; isOn: boolean; images?: ImageMap | null
}) {
  const R = 28
  const isQuestion = digit < 0
  const img = !isQuestion ? (images?.[numImgKey(digit, isOn)] ?? null) : null
  const bgColor = isOn ? '#FFD54F' : C.circleBg
  const borderColor = isOn ? '#FFD54F' : C.circleBorder

  return (
    <Group>
      <Circle x={x} y={y} radius={R} fill={bgColor} stroke={borderColor} strokeWidth={2} />
      {img ? (
        <KonvaImage x={x - R} y={y - R} width={R * 2} height={R * 2} image={img} listening={false} />
      ) : (
        <Text
          x={x - R} y={y - R} width={R * 2} height={R * 2}
          text={isQuestion ? '?' : String(digit)}
          fontSize={isQuestion ? 26 : 30} fontStyle="bold" fontFamily="CookeiRunBlack"
          fill={isOn ? '#3E2723' : C.circleQuestion} align="center" verticalAlign="middle"
        />
      )}
    </Group>
  )
}

/** 정답 빨간 동그라미 스프라이트 애니메이션 (right.png, 5×4 = 20프레임) */
const SPRITE_ANIM_FPS = SPRITE_TOTAL_FRAMES / 1.4

function RightAnimation({ x, y, images }: {
  x: number; y: number; images?: ImageMap | null
}) {
  const [frame, setFrame] = useState(0)
  const [done, setDone] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const spriteImg = images?.rightAnim ?? null

  useEffect(() => {
    if (!spriteImg || done) return
    const interval = 1000 / SPRITE_ANIM_FPS
    let current = 0
    const tick = () => {
      current++
      if (current >= SPRITE_TOTAL_FRAMES) {
        setFrame(SPRITE_TOTAL_FRAMES - 1)
        setDone(true)
        return
      }
      setFrame(current)
      timerRef.current = setTimeout(tick, interval)
    }
    timerRef.current = setTimeout(tick, interval)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [spriteImg, done])

  if (!spriteImg) return null

  const col = frame % SPRITE_COLS
  const row = Math.floor(frame / SPRITE_COLS)
  const displaySize = 120

  return (
    <KonvaImage
      x={x - displaySize / 2}
      y={y - displaySize / 2}
      width={displaySize}
      height={displaySize}
      image={spriteImg}
      crop={{
        x: col * SPRITE_FRAME_W,
        y: row * SPRITE_FRAME_H,
        width: SPRITE_FRAME_W,
        height: SPRITE_FRAME_H,
      }}
      listening={false}
    />
  )
}

/** ▲/▼ 스테퍼 + 숫자 (항상 편집 가능, 즉시 적용) */
function NumberStepper({ x, y, value, onChange }: {
  x: number; y: number; value: number; onChange?: (delta: number) => void
}) {
  const btnW = 28
  const btnH = 14
  const gap = 24

  return (
    <Group>
      <Text
        x={x - 25} y={y - 20}
        width={50} height={40}
        text={String(value)}
        fontSize={40} fontStyle="bold" fontFamily="CookeiRunBlack"
        fill={C.number} align="center" verticalAlign="middle"
        listening={false}
      />
      <Group onClick={() => onChange?.(1)} onTap={() => onChange?.(1)}>
        <Rect
          x={x - btnW / 2} y={y - gap - btnH}
          width={btnW} height={btnH}
          fill={value >= 9 ? C.stepperBgDisabled : C.stepperBg}
          cornerRadius={[4, 4, 0, 0]}
        />
        <Text
          x={x - btnW / 2} y={y - gap - btnH}
          width={btnW} height={btnH}
          text="▲" fontSize={9}
          fill={value >= 9 ? C.stepperTextDisabled : C.stepperText}
          align="center" verticalAlign="middle" listening={false}
        />
      </Group>
      <Group onClick={() => onChange?.(-1)} onTap={() => onChange?.(-1)}>
        <Rect
          x={x - btnW / 2} y={y + gap}
          width={btnW} height={btnH}
          fill={value <= 2 ? C.stepperBgDisabled : C.stepperBg}
          cornerRadius={[0, 0, 4, 4]}
        />
        <Text
          x={x - btnW / 2} y={y + gap}
          width={btnW} height={btnH}
          text="▼" fontSize={9}
          fill={value <= 2 ? C.stepperTextDisabled : C.stepperText}
          align="center" verticalAlign="middle" listening={false}
        />
      </Group>
    </Group>
  )
}
